import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import CustomAxios from "../utils/CustomAxios";
import { updateFirst_name, updateId, updateLast_name, updateMobileno, updateSelectedHub } from "../Redux/user";
import { useGetInventory } from "../Components/globalFunctions";
import { updateInventory } from "../Redux/inventory";
import { jwtDecode } from "jwt-decode";
import { prefetchAppConfig, getCurrentHubId } from "../utils/getAppConfig";
import { cacheAppConfigForSW } from "../utils/cacheAppConfigForSW";
import { updatePWAConfig } from "../utils/updatePWAConfig";
import otpIcon from '../Assets/prime/otp/otp.svg';
import './otp-verification.css';

export function OtpVerification() {
    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [resend_loading, setresendLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const getInventerory = useGetInventory();
    const inventory = useSelector(state => state.inventory.inventory);

    const inputRefs = useRef([]);
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const [timer, setTimer] = useState(180);
    const [isOtpFilled, setIsOtpFilled] = useState(false);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            toast.current.show({ severity: 'error', summary: 'OTP Expired', detail: 'Please request a new OTP.', life: 3000 });
            setOtp(new Array(6).fill(""));
        }
    }, [timer]);

    useEffect(() => {
        setIsOtpFilled(otp.join("").length === 6);
    }, [otp]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const handleChange = (index, event) => {
        const value = event.target.value;
        if (!/^[0-9]?$/.test(value)) return;

        let newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            handleOtpSubmit(event);
        }
    };

    const mobileno = localStorage.getItem("mobileno");

    useEffect(() => {
        const mobileno = localStorage.getItem('mobileno');
        if (!mobileno) {
            navigate('/');
        }
    }, [navigate]);

    // Auto-focus first OTP input on mount
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        const otpString = otp.join("");

        if (timer === 0) {
            toast.current.show({ severity: 'error', summary: 'OTP Expired', detail: 'Please request a new OTP.', life: 3000 });
            return;
        }

        if (otpString.length !== 6) {
            toast.current.show({ severity: 'error', summary: 'Invalid OTP', detail: 'OTP must be exactly 6 digits', life: 3000 });
            return;
        }

        setLoading(true);

        try {
            // Extract subdomain from hostname (e.g., keerthi.jmart.arivu-iq.in -> keerthi)
            const hostname = window.location.hostname;
            const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
            
            let subdomain = "";
            
            if (!isIP) {
                // Handle localhost pattern: subdomain.localhost
                if (hostname.endsWith('.localhost')) {
                    subdomain = hostname.replace('.localhost', '');
                }
                // Handle simple domain pattern: subdomain.domain.tld (exactly 3 parts)
                // Exclude Azure App Service domains and other multi-part domains
                else {
                    const parts = hostname.split(".");
                    if (parts.length === 3 && parts[0].toLowerCase() !== 'www') {
                        subdomain = parts[0];
                    }
                }
            }
            
            const brand = localStorage.getItem("brand") || subdomain;
            
            const otpData = { 
                mobileno: mobileno, 
                otp: otpString, 
                brand: brand
            };
            
            // Only add subdomain if it exists (not localhost/main domain)
            if (subdomain) {
                otpData.subdomain = subdomain;
            }

            const response = await CustomAxios.post(`otp-verification/`, otpData);
            const data = response.data;

            if (data.message === "active user") {
                localStorage.setItem("authtoken", data.access_token);
                localStorage.setItem("refreshToken", data.refresh_token);
                localStorage.setItem("role", data.user_data.role);
                localStorage.setItem("id", data.user_data.id);
                localStorage.setItem("first_name", data.user_data.first_name);
                localStorage.setItem("last_name", data.user_data.last_name);

                const userMobile = data.user_data?.mobileno || mobileno;
                if (userMobile) {
                    localStorage.setItem("mobileno", userMobile);
                    dispatch(updateMobileno(userMobile));
                }

                dispatch(updateFirst_name(data.user_data.first_name));
                dispatch(updateLast_name(data.user_data.last_name));
                dispatch(updateId(data.user_data.id));

                // For merchants, fetch their specific inventory only (don't fetch all inventories)
                if (data.user_data.role === 'merchant') {
                    try {
                        // Get inventory_id from OTP response - check both fields explicitly
                        // Use nullish coalescing to only use the second value if first is null/undefined
                        const otpInventoryId = data.inventory_id ?? data.user_data?.inventory_id;
                        
                        // Debug logging to see what we're getting from the API
                        console.log('OTP Verification - Merchant inventory_id check:', {
                            'data.inventory_id': data.inventory_id,
                            'data.user_data?.inventory_id': data.user_data?.inventory_id,
                            'otpInventoryId': otpInventoryId,
                            'type': typeof otpInventoryId
                        });
                        
                        // If inventory_id is null, undefined, empty string, 0, or NaN, navigate to add_hub without ID
                        if (otpInventoryId === null || 
                            otpInventoryId === undefined || 
                            otpInventoryId === '' || 
                            otpInventoryId === 0 ||
                            isNaN(otpInventoryId)) {
                            console.log('OTP Verification - inventory_id is null/invalid, navigating to /home/add_hub without ID');
                            localStorage.setItem('hub', JSON.stringify([]));
                            localStorage.setItem('selected_hub', '');
                            dispatch(updateSelectedHub([]));
                            navigate("/home/add_hub");
                            setLoading(false);
                            return;
                        }
                        
                        // Convert to number to ensure consistency
                        const merchantHubId = Number(otpInventoryId);
                        
                        // Double check after conversion - if it's 0 or NaN, don't proceed
                        if (merchantHubId === 0 || isNaN(merchantHubId)) {
                            localStorage.setItem('hub', JSON.stringify([]));
                            localStorage.setItem('selected_hub', '');
                            dispatch(updateSelectedHub([]));
                            navigate("/home/add_hub");
                            setLoading(false);
                            return;
                        }
                        
                        // Call inventory/view API to get the hub details
                        const merchantHubResponse = await CustomAxios.get('inventory/view');
                        const merchantHubList = merchantHubResponse?.data || [];
                        
                        // Filter to only include the inventory that matches the inventory_id from OTP response
                        const merchantHub = merchantHubList.find(hub => hub.id === merchantHubId);
                        
                        // If hub found in API response, use its details; otherwise create a placeholder
                        let inventoryOption;
                        if (merchantHub) {
                            // Convert to the format expected by Redux inventory state
                            inventoryOption = {
                                name: merchantHub.place,
                                value: merchantHub.id,
                                supervisors: merchantHub.supervisors || [],
                                admins: merchantHub.admins || [],
                                customer_engagement_admins: merchantHub.customer_engagement_admins || [],
                                cashier_ids: merchantHub.cashier_ids || [],
                                pincodes: merchantHub.pincodes || [],
                                franchise: merchantHub.franchise,
                                delivery_fee: merchantHub.delivery_fee,
                                self_pickup_waiver: merchantHub.self_pickup_waiver,
                                self_pickup_waiver_percentage: merchantHub.self_pickup_waiver_percentage,
                                mobileno: merchantHub.mobileno,
                                email: merchantHub.email || '',
                                delivery_status: merchantHub.delivery_status,
                                delivery_mini_spent: merchantHub.delivery_mini_spent,
                                shop_open_at: merchantHub.shop_open_at,
                                shop_close_at: merchantHub.shop_close_at,
                                hub_setup: merchantHub.hub_setup,
                                hub_link: merchantHub.hub_link || '',
                                is_approved: merchantHub.is_approved || false
                            };
                        } else {
                            // Hub not in API response (might be pending approval), create placeholder
                            inventoryOption = {
                                name: `Hub ${merchantHubId}`,
                                value: merchantHubId,
                                place: `Hub ${merchantHubId}`,
                                supervisors: [],
                                admins: [],
                                customer_engagement_admins: [],
                                cashier_ids: [],
                                pincodes: [],
                                franchise: null,
                                delivery_fee: '',
                                self_pickup_waiver: false,
                                self_pickup_waiver_percentage: '',
                                mobileno: '',
                                email: '',
                                delivery_status: '',
                                delivery_mini_spent: '',
                                shop_open_at: null,
                                shop_close_at: null,
                                hub_setup: '',
                                hub_link: '',
                                is_approved: false
                            };
                        }
                        
                        // Update Redux inventory state with only this one inventory
                        dispatch(updateInventory([inventoryOption]));
                        
                        // Set the merchant's actual hub in localStorage using the inventory_id from OTP response
                        localStorage.setItem('hub', JSON.stringify([merchantHubId]));
                        localStorage.setItem('selected_hub', merchantHubId.toString());
                        dispatch(updateSelectedHub([merchantHubId]));
                        
                        // Update PWA config immediately after setting hub
                        try {
                            await prefetchAppConfig(merchantHubId);
                            await cacheAppConfigForSW();
                            await updatePWAConfig(true);
                            // console.log('[OTP Verification] PWA config updated after login for merchant hub:', merchantHubId);
                        } catch (e) {
                            await updatePWAConfig(false);
                        }
                        
                        // Navigate directly to merchant's hub page using the inventory_id from OTP response
                        navigate(`/home/add_hub/${merchantHubId}`);
                        setLoading(false);
                        return;
                    } catch (merchantError) {
                        navigate("/home/add_hub");
                        setLoading(false);
                        return;
                    }
                }
                
                // For non-merchants, fetch all inventories and use the existing logic
                if (!inventory || inventory.length === 0) {
                    await getInventerory();
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
                
                let selectedInventoryId = null;
                
                if (data.inventory_id) {
                    selectedInventoryId = data.inventory_id;
                } else if (data.user_data && data.user_data.inventory_id) {
                    selectedInventoryId = data.user_data.inventory_id;
                } else if (Array.isArray(data.inventory_ids) && data.inventory_ids.length > 0) {
                    selectedInventoryId = data.inventory_ids[0];
                }
                
                if (selectedInventoryId) {
                    localStorage.setItem('hub', JSON.stringify([selectedInventoryId]));
                    localStorage.setItem('selected_hub', selectedInventoryId.toString());
                    dispatch(updateSelectedHub([selectedInventoryId]));
                    
                    // Update PWA config immediately after setting hub
                    try {
                        await prefetchAppConfig(selectedInventoryId);
                        await cacheAppConfigForSW();
                        await updatePWAConfig(true);
                        // console.log('[OTP Verification] PWA config updated after login for hub:', selectedInventoryId);
                    } catch (e) {
                        await updatePWAConfig(false);
                    }
                } else if (Array.isArray(data.inventory_ids) && data.inventory_ids.length > 0) {
                    localStorage.setItem('hub', JSON.stringify(data.inventory_ids));
                    localStorage.setItem('selected_hub', JSON.stringify(data.inventory_ids));
                    dispatch(updateSelectedHub(data.inventory_ids));
                    
                    // Update PWA config for first hub in the array
                    const firstHubId = data.inventory_ids[0];
                    if (firstHubId) {
                        try {
                            await prefetchAppConfig(firstHubId);
                            await cacheAppConfigForSW();
                            await updatePWAConfig(true);
                            // console.log('[OTP Verification] PWA config updated after login for hub:', firstHubId);
                        } catch (e) {
                            await updatePWAConfig(false);
                        }
                    }
                } else {
                    localStorage.setItem('hub', JSON.stringify([]));
                    localStorage.setItem('selected_hub', JSON.stringify([]));
                    dispatch(updateSelectedHub([]));
                    await updatePWAConfig(false);
                }

                if (data.user_data.role === 'cashier') {
                    navigate("/home/order_management");
                } else if (data.user_data.role === 'user') {
                    try {
                        const hostname = window.location.hostname; // e.g., nilgiris.localhost
                        const parts = hostname.split(".");
                        let subdomain = "";

                        if (parts.length >= 2 && parts[0].toLowerCase() !== "localhost") {
                            subdomain = parts[0].toLowerCase();
                        }


                        if (subdomain) {
                            const inventoryRes = await CustomAxios.get("inventory/view");
                            const inventoryList = inventoryRes?.data || [];

                            // Try to match hub_link or place with the subdomain
                            const matchingHub = inventoryList.find((hub) => {
                                const hubLink = (hub.hub_link || "").toLowerCase();
                                const place = (hub.place || "").toLowerCase();
                                return hubLink.includes(subdomain) || place.includes(subdomain);
                            });

                            if (matchingHub && matchingHub.id) {
                                const hubIdFromSubdomain = matchingHub.id;

                                localStorage.setItem("hub", JSON.stringify([hubIdFromSubdomain]));
                                localStorage.setItem("selected_hub", hubIdFromSubdomain.toString());
                                dispatch(updateSelectedHub([hubIdFromSubdomain]));

                                // Preload AppConfig and update PWA/assets for this hub
                                await prefetchAppConfig(hubIdFromSubdomain);
                                await cacheAppConfigForSW();
                                await updatePWAConfig(true);
                            } else {
                                console.warn("[OTP user] No matching hub found for subdomain:", subdomain);
                            }
                        } else {
                            console.warn("[OTP user] No subdomain resolved from hostname, skipping hub override");
                        }
                    } catch (e) {
                        console.warn("[OTP user] Failed to bind hub based on subdomain:", e);
                    }

                    navigate("/home/user_management");
                } else {
                    navigate("/home/add_hub");
                }
            } else {
                toast.current.show({ severity: "error", summary: "Error", detail: data.message, life: 3000 });
            }
        } catch (error) {
            if (error.response) {
                const errorMessage = error.response.data?.message || "Invalid OTP";
                toast.current.show({ severity: "error", summary: "Error", detail: errorMessage, life: 3000 });
            } else {
                toast.current.show({ severity: "error", summary: "Error", detail: "Network Error", life: 3000 });
            }
        }

        setLoading(false);
    };

    const handleResendOtp = async () => {
        if (timer > 0) return;

        const mobileno = localStorage.getItem("mobileno");

        if (!mobileno) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Mobile number is required', life: 3000 });
            return;
        }

        setresendLoading(true);

        try {
            // Extract subdomain from hostname
            const hostname = window.location.hostname;
            const hostnameParts = hostname.split(".");
            
            // Determine if subdomain exists (not localhost, IP, or main domain)
            const isLocalhostOrIP = hostname === 'localhost' || 
                                   hostname === '127.0.0.1' || 
                                   (hostname.replace(/\./g, '').match(/^\d+$/) && hostnameParts.length === 4);
            const isExactlyTwoParts = hostnameParts.length === 2;
            const isWwwDomain = hostnameParts.length === 3 && hostnameParts[0].toLowerCase() === 'www';
            
            const hasSubdomain = hostnameParts.length > 2 && !isLocalhostOrIP && !isWwwDomain;
            const subdomain = hasSubdomain ? hostnameParts[0] : "";
            
            const resendData = { mobileno };
            
            // Only add subdomain if it exists (not localhost/main domain)
            if (subdomain) {
                resendData.subdomain = subdomain;
            }
            
            const response = await CustomAxios.post(`regenerate-otp/`, resendData);
            const data = response.data;

            if (data.message === "New OTP has been sent to your new mobile number.") {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'OTP has been resent.', life: 3000 });
                setOtp(new Array(6).fill(""));
                inputRefs.current[0]?.focus();
                setTimer(180);
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: data.message, life: 3000 });
            }
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to resend OTP', life: 3000 });
        }

        setresendLoading(false);
    };

    useEffect(() => {
        const token = localStorage.getItem('authtoken');
        if (token) {
            navigate("/home/product_details");
        }
    }, []);

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        if (phone.length === 10) {
            return `+91 ${phone}`;
        }
        return phone;
    };

    return (
        <div className="otp-container">
            <Toast ref={toast} />
            <div className="otp-card">
                {/* Padlock Icon */}
                <div className="otp-icon-wrapper">
                    <img src={otpIcon} alt="OTP Verification" className="otp-icon" />
                </div>

                {/* Title */}
                <div className="otp-instructions">
                <h1 className="otp-title">Verify OTPsss</h1>

                {/* Instructions */}
                    <p className="instruction-text">
                        Enter the 6-digit code sent to
                    </p>
                    <p className="phone-number">{formatPhoneNumber(mobileno)}</p>
                </div>

                {/* OTP Input Fields */}
                <form onSubmit={handleOtpSubmit} className="otp-form">
                    <div className="otp-inputs-wrapper">
                        {otp.map((value, index) => (
                            <InputText
                                key={index}
                                value={value}
                                onChange={(e) => handleChange(index, e)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                ref={(el) => (inputRefs.current[index] = el)}
                                maxLength={1}
                                className="otp-input"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                disabled={loading}
                            />
                        ))}
                    </div>

                    {/* Verify Button */}
                    <Button
                        type="submit"
                        disabled={!isOtpFilled || loading}
                        className="verify-button"
                        loading={loading}
                    >
                        {!loading && <span>Verify and continue</span>}
                    </Button>
                </form>

                {/* Change Mobile Number Link */}
                <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="change-mobile-link"
                >
                    Change Mobile Number
                </button>
            </div>
        </div>
    );
}
